import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as selector from '../../selectors/selectors';
import { FalkonSmsLogoIcon } from "../../icon";
import ReactCodeInput from "react-code-input";
import ButtonComponent from "../shared/button/button.component";
import { SecureDocsService } from "../../services/SecureDocsService";
import { useLocation, useNavigate } from "react-router-dom";
import { ToasterType } from "../shared/toaster/toaster.model";
import setToast from "../../helpers/toaster.helper";
import { AppRoutes } from "../routes/route.path";

const FalkonSecureDocOtp: React.FC = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search)
    const recipientId = params.get("id")
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const [otp, setOtp] = useState<{ value: string, show: boolean, error: string }>({ value: "", show: false, error: "" });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [timer, setTimer] = useState(0);
    const [canResend, setCanResend] = useState(false);
    const otpSentRef = useRef(false);

    const handleOtpChange = (value: string) => {
        setOtp((prev) => ({
        ...prev,
        value,
         error: "", 
        })); 
    };

    const otpPayload = {
        otp: otp.value
    };

    const handleSubmitOtp = () => {
        setIsLoading(true); 
        SecureDocsService.openSecureDocWithOtp(recipientId, otpPayload)
            .then((res) => {
                if (res?.data) {
                    setToast("The document has been successfully opened for signing.", ToasterType.SUCCESS);
                    navigate(AppRoutes.FALKON_DOCS_END_USER_SIGN, {
                        state: { response: res.data },
                    });
                } else {
                    setToast("Missing file ID or token in response.", ToasterType.DANGER);
                    setIsLoading(false); 
                }
            })
            .catch((err) => {
                setIsLoading(false); 
                setOtp((prev) => ({
                    ...prev,
                    error: "Invalid OTP. Please try again.", 
                }));
                setToast("Failed to verify OTP. Please try again.", ToasterType.DANGER);
            });
    };

    useEffect(() => {
        if (recipientId && !otpSentRef.current) {
            SendOtp(); 
        }
    }, [recipientId]); 

    const SendOtp = () => {
        if (otpSentRef.current) {
            return;
        }

        otpSentRef.current = true;
        SecureDocsService.sendSecureDocOtp(recipientId)
            .then((res) => {
                setToast(res.data.message, ToasterType.SUCCESS);
                localStorage.setItem(`${recipientId}-resendCountDown`, Date.now().toString());
                setTimer(120);
                setCanResend(false);
            })
            .catch((err) => {
                otpSentRef.current = false; 
                const isAlreadyCompleted = err?.response?.data?.error?.includes("already completed");
                const isNotActive = err?.response?.data?.message?.includes("document is not active");

                if (isAlreadyCompleted) {
                    const state = { isComplete: true };
                    navigate(AppRoutes.FALKON_DOCS_END_USER_SIGN_SUCCESS, { state });
                } else if (isNotActive) {
                    const state = { isNotActive: true };
                    navigate(AppRoutes.FALKON_DOCS_END_USER_SIGN_SUCCESS, { state });
                } else {
                    setToast("Failed to open document with OTP.", ToasterType.DANGER);
                }
            });
    };

    const handleResendOtp = () => {
        otpSentRef.current = false;
        SendOtp();
    };

    useEffect(() => {
        const saved = localStorage.getItem(`${recipientId}-resendCountDown`);
        if (saved) {
            const elapsed = Math.floor((Date.now() - Number(saved)) / 1000);
            const timeLeft = 120 - elapsed;
            if (timeLeft > 0) {
                setTimer(timeLeft);
                setCanResend(false);
            } else {
                setTimer(0);
                setCanResend(true);
            }
        } else {
            setCanResend(true);
        }
    }, [recipientId]);

    useEffect(() => {
        if (timer <= 0) return;

        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setCanResend(true);
                    localStorage.removeItem(`${recipientId}-resendCountDown`)
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [timer]);

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="success-page-text pt-2 pl-3 color-primary-accent h4-semibold">Loading document for signing...</p>
            </div>
        );
    }

    return (
        <div className={`w-p-100 ${IsMobileDevice ? "h-v-90" : "h-v-100"} d-flex flex-direction justify-between align-center`}>
            <div className={`${IsMobileDevice ? "w-p-100 h-p-100" : "w-p-100 h-p-92"} d-flex flex-direction`}>
                    <div className={`d-flex justify-center flex-direction align-center w-p-100 my-auto pb-8`}>
                        <div className="mx-auto h1-medium py-1">
                            Please Verify Your Identity
                        </div>
                        <div className="mx-auto h4-medium color-secure-chat-login-pin">
                            A one-time OTP has been sent.
                        </div>

                        <div className="mx-auto d-flex">
                            <ReactCodeInput
                                name={"EnterOtp"}
                                value={otp.value}
                                type={otp.show ? "number" : "password"}
                                fields={6}
                                inputMode="numeric"
                                onChange={handleOtpChange}
                                disabled={isLoading}
                                inputStyle={{ border: "none", borderBottom: otp.error ? "2px solid red" : "2px solid #1450FF", margin: "10px", font: "normal normal 500 24px/36px silkamedium", color: otp.error ? "red" : "#7B7B7B", width: IsMobileDevice ? "16px" : "40px", height: "40px", textAlign: "center" }}
                            />
                        </div>
                        {canResend ?
                            <div className={`mx-auto py-1 color-primary-light c-pointer h5-medium`} onClick={handleResendOtp}>
                                Resend
                            </div>
                            :
                            <div className={`mx-auto py-1 color-primary-accent h5-medium`}>
                                {`Resend in ${timer}`}
                            </div>
                        }
                        <ButtonComponent ButtonLabel={"Confirm"} ClassName={`${IsMobileDevice ? "min-w-16" : "w-p-20"} w-p-20 h3-regular unauth-screen-button-gredient`} Variant={otp.value.length === 6 && !otp.error ? "primary-btn" : "disabled-btn"} OnClick={handleSubmitOtp}/>
                    </div>     
            </div>

            {!IsMobileDevice &&
                <div className={`w-p-100 h-p-8 h5-medium d-flex justify-center align-center ${IsMobileDevice ? "" : "bg-color-secure-chat-footer"} color-secure-file-email-filename`}>
                    <FalkonSmsLogoIcon className="mr-1 w-4 h-4" /> Secured by Falkon SMS
                </div>
            }
           
        </div >
    )
}

export default FalkonSecureDocOtp;